/**
 * Case-level calibration feedback — human comments vs Ace reasons, misaligned reviews.
 */

import { useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faChevronDown,
  faChevronRight,
  faComment,
  faRobot,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type {
  CalibrationResolutionOutcome,
  CheckDisagreementDetail,
  MisalignedReviewItem,
  RagCounts,
} from "@/services/api/agentReview";
import {
  viewSubmittedCaseIntent,
  type ReviewCaseOpenIntent,
} from "./reviewCaseOpenIntent";
import { CalibrationResolutionControls } from "./CalibrationResolutionControls";
import { ragOption } from "./RagOption";
import { answerLabel, caseDisplayLabel, disputeResolutionKey, isArtefactPhase, phaseLabel } from "./calibrationAnalysisHelpers";
import type { Rag } from "./types";

function AnswerBadge({ answer, side }: { answer: string; side: "human" | "ace" }) {
  return (
    <span
      className={clsx(
        "inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium ring-1",
        answer === "pass" && "bg-emerald-500/10 text-emerald-200 ring-emerald-500/25",
        answer === "fail" && "bg-rose-500/10 text-rose-200 ring-rose-500/25",
        answer === "na" && "bg-slate-500/10 text-slate-300 ring-slate-500/25",
        side === "human" && answer !== "pass" && answer !== "fail" && answer !== "na" && "bg-slate-500/10 text-slate-300",
      )}
    >
      {answerLabel(answer as "pass" | "fail" | "na")}
    </span>
  );
}

function RagMini({ rag }: { rag: Rag | null }) {
  if (!rag) return <span className="text-slate-600">—</span>;
  const opt = ragOption(rag);
  return (
    <span
      className={clsx(
        "inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium ring-1",
        rag === "green" && "bg-emerald-500/10 text-emerald-200 ring-emerald-500/25",
        rag === "amber" && "bg-amber-500/10 text-amber-200 ring-amber-500/25",
        rag === "red" && "bg-rose-500/10 text-rose-200 ring-rose-500/25",
      )}
    >
      {opt.label}
    </span>
  );
}

function RagDistribution({ label, counts }: { label: string; counts: RagCounts }) {
  const total = counts.green + counts.amber + counts.red;
  if (total === 0) return null;

  return (
    <div className="space-y-1">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <div className="flex flex-wrap gap-2 text-xs text-slate-400">
        <span>
          <RagMini rag="green" /> {counts.green}
        </span>
        <span>
          <RagMini rag="amber" /> {counts.amber}
        </span>
        <span>
          <RagMini rag="red" /> {counts.red}
        </span>
      </div>
    </div>
  );
}

export function HumanRagOverview({
  humanOrchRags,
  humanOutRags,
}: {
  humanOrchRags: RagCounts;
  humanOutRags: RagCounts;
}) {
  const hasData =
    humanOrchRags.green + humanOrchRags.amber + humanOrchRags.red > 0 ||
    humanOutRags.green + humanOutRags.amber + humanOutRags.red > 0;

  if (!hasData) return null;

  return (
    <section
      className="rounded-xl bg-white/[0.03] ring-1 ring-white/5 px-4 py-3"
      data-testid="calibration-human-rag-overview"
    >
      <p className="text-sm font-medium text-slate-200 mb-3">Human RAG distribution</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <RagDistribution label="Orchestrate" counts={humanOrchRags} />
        <RagDistribution label="Output" counts={humanOutRags} />
      </div>
    </section>
  );
}

export function CheckDisagreementCard({
  item,
  onOpenCase,
  onCompareArtefact,
  savingResolution = false,
  onResolve,
  onClearResolution,
}: {
  item: CheckDisagreementDetail;
  onOpenCase: (runId: string, label?: string, intent?: ReviewCaseOpenIntent) => void;
  onCompareArtefact?: (item: CheckDisagreementDetail) => void;
  savingResolution?: boolean;
  onResolve?: (item: CheckDisagreementDetail, outcome: CalibrationResolutionOutcome, note: string) => void;
  onClearResolution?: (item: CheckDisagreementDetail) => void;
}) {
  const showCompare = isArtefactPhase(item.phase) && onCompareArtefact;

  return (
    <div
      className="rounded-lg bg-white/[0.03] ring-1 ring-white/5 overflow-hidden"
      data-testid="calibration-check-disagreement"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 px-3 py-2">
        <div className="min-w-0">
          <p className="text-sm text-slate-200 truncate">{caseDisplayLabel(item)}</p>
          <p className="text-xs text-slate-500">
            <span>{phaseLabel(item.phase)}</span>
            <span className="text-slate-600"> · </span>
            {item.check_label}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {showCompare ? (
            <button
              type="button"
              onClick={() => onCompareArtefact(item)}
              className="text-xs text-indigo-300 hover:text-indigo-200"
            >
              Compare
            </button>
          ) : null}
          <button
            type="button"
            onClick={() =>
              onOpenCase(item.run_id, item.subject_label || undefined, viewSubmittedCaseIntent(item))
            }
            className="text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1"
          >
            View case
            <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
          </button>
        </div>
      </div>

      <div className="grid gap-3 p-3 sm:grid-cols-2">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faComment} className="text-slate-500 text-xs" />
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Human
            </span>
            <AnswerBadge answer={item.human_answer} side="human" />
          </div>
          {item.human_comment?.trim() ? (
            <p className="text-xs text-slate-300 leading-relaxed pl-3 border-l-2 border-slate-600">
              {item.human_comment}
            </p>
          ) : (
            <p className="text-xs text-slate-600 italic">No comment</p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faRobot} className="text-indigo-400 text-xs" />
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Ace
            </span>
            <AnswerBadge answer={item.ace_answer} side="ace" />
          </div>
          {item.ace_reason?.trim() ? (
            <p className="text-xs text-slate-400 leading-relaxed pl-3 border-l-2 border-indigo-500/40">
              {item.ace_reason}
            </p>
          ) : (
            <p className="text-xs text-slate-600 italic">No reason recorded</p>
          )}
        </div>
      </div>

      {onResolve && onClearResolution ? (
        <div className="border-t border-white/5 px-3 py-3">
          <CalibrationResolutionControls
            item={item}
            saving={savingResolution}
            onResolve={(outcome, note) => onResolve(item, outcome, note)}
            onClear={() => onClearResolution(item)}
          />
        </div>
      ) : null}
    </div>
  );
}

export function CalibrationCheckDisagreementsSection({
  disagreements,
  onOpenCase,
  onCompareArtefact,
  savingResolutionKey = null,
  onResolve,
  onClearResolution,
}: {
  disagreements: CheckDisagreementDetail[];
  onOpenCase: (runId: string, label?: string, intent?: ReviewCaseOpenIntent) => void;
  onCompareArtefact?: (item: CheckDisagreementDetail) => void;
  savingResolutionKey?: string | null;
  onResolve?: (item: CheckDisagreementDetail, outcome: CalibrationResolutionOutcome, note: string) => void;
  onClearResolution?: (item: CheckDisagreementDetail) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  if (disagreements.length === 0) return null;

  return (
    <section
      className="rounded-xl bg-white/[0.03] ring-1 ring-white/5 overflow-hidden"
      data-testid="calibration-check-disagreements"
    >
      <button
        type="button"
        onClick={() => setExpanded((current) => !current)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-white/[0.02]"
      >
        <FontAwesomeIcon
          icon={expanded ? faChevronDown : faChevronRight}
          className="mt-1 text-slate-500 text-xs shrink-0"
        />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-medium text-slate-200">Case-by-case feedback</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {disagreements.length} check{" "}
            {disagreements.length === 1 ? "disagreement" : "disagreements"} — expand to browse
            every case
          </p>
        </div>
      </button>
      {expanded ? (
        <div className="border-t border-white/5 px-4 py-4 space-y-2">
          {disagreements.map((item) => (
            <CheckDisagreementCard
              key={disputeResolutionKey(item)}
              item={item}
              onOpenCase={onOpenCase}
              onCompareArtefact={onCompareArtefact}
              savingResolution={savingResolutionKey === disputeResolutionKey(item)}
              onResolve={onResolve}
              onClearResolution={onClearResolution}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

export function CalibrationMisalignedReviewsSection({
  items,
  onOpenCase,
}: {
  items: MisalignedReviewItem[];
  onOpenCase: (runId: string, label?: string, intent?: ReviewCaseOpenIntent) => void;
}) {
  if (items.length === 0) return null;

  return (
    <section
      className="rounded-xl bg-white/[0.03] ring-1 ring-white/5 overflow-hidden"
      data-testid="calibration-misaligned-reviews"
    >
      <div className="border-b border-white/5 px-4 py-3">
        <h3 className="text-sm font-medium text-slate-200">Misaligned reviews</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Cases where human and Ace picked different overall RAGs on at least one phase
        </p>
      </div>
      <ul className="divide-y divide-white/5">
        {items.map((item) => (
          <li key={item.review_id} className="px-4 py-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm text-slate-200">{item.subject_label || "Case"}</p>
                {item.check_disagreement_count > 0 ? (
                  <p className="text-xs text-amber-200/80">
                    {item.check_disagreement_count} check{" "}
                    {item.check_disagreement_count === 1 ? "disagreement" : "disagreements"}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() =>
                  onOpenCase(item.run_id, item.subject_label || undefined, { viewSubmitted: true })
                }
                className="text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1"
              >
                View case
                <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
              </button>
            </div>
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center gap-2">
                <span className="text-slate-500">Orch</span>
                <RagMini rag={item.orch_human_rag} />
                <span className="text-slate-600">vs</span>
                <RagMini rag={item.orch_ace_rag} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500">Output</span>
                <RagMini rag={item.out_human_rag} />
                <span className="text-slate-600">vs</span>
                <RagMini rag={item.out_ace_rag} />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
